import { useState, useRef, useEffect } from "react";
import { BarChart, XAxis, YAxis, Tooltip, Bar } from "recharts";
import { FaCheck } from "react-icons/fa6";
import { IoCameraSharp } from "react-icons/io5";
import { PiCalendarXBold, PiChatsTeardropBold } from "react-icons/pi";
import { PiTrendUp } from "react-icons/pi";
import { FiEdit } from "react-icons/fi";
import { IoCloseOutline } from "react-icons/io5";
import { IoMdAdd } from "react-icons/io";
import styles from "./TherapistProfile.module.css";
import Rating from "@material-ui/lab/Rating";
import Select from "react-select";
import languages from "assets/JSONs/Languages/languages.json";
import { getTherapistProfile } from "actions/therapist";
import { useUserTokenSelector } from "store/reducers";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "store/actions";
import axios from "axios";

const rangeFilterOptions = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "15d", label: "Last 15 Days" },
  { value: "1m", label: "Last Month" },
  { value: "3m", label: "Last 3 Months" },
  { value: "6m", label: "Last 6 Months" },
  // { value: "custom", label: "Custom Range" }, // For future implementation with date pickers
];

const TherapistProfile = () => {

  const [photoUrl, setPhotoUrl] = useState("");
  const [noOfClients, setNoOfClients] = useState("");
  const [completedSessions, setCompletedSessions] = useState('');
  const [selectedRangeFilter, setSelectedRangeFilter] = useState("all");
  const [noOfRepeatClients, setNoOfRepeatClients] = useState();
  const [noOfNewClients, setNoOfNewClients] = useState('');
  const [info, setInfo] = useState([]);
  const [exp, setExp] = useState("");
  const [bio, setBio] = useState("");
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [editExp, setEditExp] = useState(false);
  const [editBio, setEditBio] = useState(false);
  const [editLanguage, setEditLanguage] = useState(false);
  const userToken = useUserTokenSelector();
  const dispatch = useDispatch();
  const [notifications, setNotifications] = useState([]);
  const [ratingSize, setRatingSize] = useState("medium"); // Default size

  useEffect(() => {
    const checkScreenSize = () => {
      if (window.matchMedia("(min-width: 787px) and (max-width: 1049px)").matches) {
        setRatingSize("small");
      } else {
        setRatingSize("medium"); // Or whatever default size you prefer outside this range
      }
    };

    checkScreenSize(); // Check on initial render
    window.addEventListener("resize", checkScreenSize);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  useEffect(() => {
    getTherapistProfileData();
  }, [selectedRangeFilter]);

  const getTherapistProfileData = async () => {
    dispatch(startLoading());
    try {
      const response = await getTherapistProfile(
        selectedRangeFilter,
        userToken
      );
      if (response.status === 200) {
        setInfo(response.data);
        setExp(response.data.experienceInYears);
        setBio(response.data.bio);
        setPhotoUrl(response.data.image);
        setSelectedLanguages(response.data.languages);
        setNoOfClients(response.data.totalClients);
        setCompletedSessions(response.data.completedBookings);
        setNoOfRepeatClients(response.data.repeatClients);
        setNoOfNewClients(response.data.oneTimeClients);
        // Dispatch action to update Redux store
        dispatch({ type: 'UPDATE_USER_AVATAR', payload: response.data.image });
        dispatch(stopLoading());
      }
    } catch (error) {
      dispatch(stopLoading());
      console.error("Error getting profile", error);
    }
  };

  const updateProfileOnBackend = async (updateData) => {
    dispatch(startLoading());
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_SERVER_URI}/api/therapist/profile`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      if (response.status === 200) {
        setBio(response.data.therapist.bio);
        setExp(response.data.experienceInYears);
        setInfo((prev) => ({ ...prev, ...response.data.therapist }))
      }
    } catch (error) {
      console.error("Error updating profile:", error.response?.data?.error || error.message);
      // Optionally show an error message to the user
    } finally {
      dispatch(stopLoading());
    }
  };

  const fileInputRef = useRef(null);

  const avatar = {
    female: "https://ucarecdn.com/c22b8f55-8c68-411e-a5c9-8b5b99c08701/femaleEmoji.svg",
    male: "https://ucarecdn.com/52c99efd-cba4-4fe1-93f2-e393088b7a0c/male.svg",
    other: "https://ucarecdn.com/c7bb4c01-2258-428d-b8ce-e2196ee63220/lgbtq.svg",
  };
  const handleEditExp = () => {
    setEditExp(!editExp);
  };
  const handleSaveExp = () => {
    const temp = info;
    temp.bio = bio;
    setInfo(temp);
    updateProfileOnBackend({ bio: bio });
    setEditBio(!editBio);
  }
  const handleEditBio = () => {
    setEditBio(!editBio);
  };
  const handleSaveBio = () => {
    const temp = info;
    temp.bio = bio;
    setInfo(temp);
    updateProfileOnBackend({ bio: bio });
    setEditBio(!editBio);
  }
  const handleEditLanguage = () => {
    setEditLanguage(!editLanguage);
  };

  const handleSaveLanguage = () => {
    const temp = { ...info };
    const languageStringsToAdd = selectedLanguages.map((lang) => lang.label);
    const currentLanguages = info.languages || [];
    // Combine and ensure uniqueness
    const updatedLanguages = [...new Set([...currentLanguages, ...languageStringsToAdd])];
    setInfo((prevInfo) => ({ ...prevInfo, languages: updatedLanguages }));
    updateProfileOnBackend({ languages: updatedLanguages });
    setSelectedLanguages([]);
    setEditLanguage(!editLanguage);
  }

  const handleRemoveLanguage = (languageToRemove) => {
    const updatedLanguages = info.languages.filter((lang, index) => index !== languageToRemove);
    setInfo((prevInfo) => ({ ...prevInfo, languages: updatedLanguages }));
    updateProfileOnBackend({ languages: updatedLanguages });
  };
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setPhotoUrl(base64String); // Update UI optimistically
        updateProfileOnBackend({ image: base64String }); // Send base64 to backend
      };
      reader.readAsDataURL(file);
    }
  };
  const handleLanguageChange = (language) => {
    setSelectedLanguages(language);
  };
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_URI}/api/getnotifications`,
          {},
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
            },
          }
        );
        setNotifications(response.data.notifications);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    fetchNotifications();
  }, []);
  return (
    <div className={styles["therapist-profile"]}>
      <div className={styles["profile-left"]}>
        <div className={`${styles["client-filter"]} ${styles.prof}`}>
          <select
            value={selectedRangeFilter}
            onChange={(e) => setSelectedRangeFilter(e.target.value)}
          >
            {rangeFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
                </option>
            ))}
          </select>
        </div>
        <div className={styles["mobile-info"]}>
          <div className={styles["analysis-div"]}>
            <p>My Clients</p>
            <div className={styles["analysis-value"]}>
              {noOfClients}
            </div>
          </div>
          <div className={styles["analysis-div"]}>
            <p>No. of Completed Sessions</p>
            <div className={styles["analysis-value"]}>
              {completedSessions}
            </div>
          </div>
          <div className={styles["analysis-div"]}>
            <p>One Time Clients</p>
            <div className={styles["analysis-value"]}>
              {noOfNewClients}
            </div>
          </div>
          <div className={styles["analysis-div"]}>
            <p>Repeat Clients</p>
            <div className={styles["analysis-value"]}>
              {noOfRepeatClients}
            </div>
          </div>
        </div>
        <div className={styles["analytics-div"]}>
          <p className={styles["analytics-head"]}>My Analytics</p>
          <div className={styles["client-chart-div"]}>
            <div className={styles["chart-legends"]}>
              <div className={styles["chart-legend"]}>
                <div className={`${styles["legend-icon"]} ${styles.new}`}></div>
                <span>One Time Clients</span>
              </div>
              <div className={styles["chart-legend"]}>
                <div className={styles["legend-icon"]}></div>
                <span>Repeat Clients</span>
              </div>
            </div>
              <div className={styles["client-chart"]}>
                <div style={{ width: info.graphData?.length * 60 }}>
                {info.graphData && info.graphData?.length > 0 ? (
                  <>
                  <div>
                    <div style={{ width: info.graphData.length * 60 }}>
                      <BarChart
                        width={info.graphData.length * 60}
                        height={250}
                        data={info.graphData}
                        barGap={0}
                        barCategoryGap={10}
                      >
                        <XAxis dataKey="period" axisLine={false} tickLine={false} />
                        <YAxis hide={true} />
                        <Tooltip />
                        <Bar dataKey="newClients" name="One Time Clients" fill="#7E8ED9" />
                        <Bar dataKey="repeatClients" name="Repeat Clients" fill="#F6D169" />
                      </BarChart>
                    </div>
                  </div>
                  </>
                  ) : (
                    <p>No data available for the selected range.</p>
                  )}
                </div>
              </div>
            {/* </div> */}
          </div>
        </div>
        <div className={styles["info-divs"]}>
          <div className={styles["info-div"]}>
            <div className={styles["info-top"]}>
              <span>No. of Sessions Completed</span>
            </div>
            <div className={styles["info-bottom"]}>
              <p className={styles["info-data"]}>{info.completedBookings}</p>
            </div>
          </div>
          <div className={styles["info-div"]}>
            <div className={styles["info-top"]}>
              <div className={styles["info-topl"]}>
                <span>My Clients</span>
              </div>
            </div>
            <div className={styles["info-bottom"]}>
              <p className={styles["info-data"]}>{info.totalClients}</p>
            </div>
          </div>
          <div className={styles["info-div"]}>
            <div className={styles["info-top"]}>
              <span>One Time Clients</span>
            </div>
            <div className={styles["info-bottom"]}>
              <p className={styles["info-data"]}>
                {noOfNewClients}
              </p>
            </div>
          </div>
          <div className={styles["info-div"]}>
            <div className={styles["info-top"]}>
              <span>Repeat Clients</span>
            </div>
            <div className={styles["info-bottom"]}>
              <p className={styles["info-data"]}>
                {noOfRepeatClients}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className={styles["profile-right"]}>
        <div className={styles["tprofile-div"]}>
          <div className={styles["show-profile"]}>
            <div className={styles["img-div"]}>
              <img src={photoUrl} alt="Profile" />
            </div>
            <input
              id="photo-upload"
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              style={{ display: "none" }}
              accept="image/*"
            />
            <label
              htmlFor="photo-upload"
              // onClick={handleCameraIconClick}
              className="add-photo"
            >
              <IoCameraSharp
                size={20}
                style={{ marginTop: "-40px", marginLeft: "75px" }}
              />
            </label>
          </div>
          <p className={styles["tname"]}>{info.fullName}</p>
          <div className={styles["exp-rating-div"]}>
            <div className={styles["texp"]}>
              <span>
                Experience-{" "}
                {editExp ? (
                  <input
                    type="text"
                    className={styles["expInput"]}
                    onChange={(e) => {
                      setExp(e.target.value);
                    }}
                    value={exp}
                  />
                ) : (
                  <span>{info.experienceInYears}</span>
                )}
                yrs
              </span>
              {editExp ? (
                <FaCheck onClick={handleSaveExp} />
              ) : (
                <FiEdit onClick={handleEditExp} />
              )}
            </div>
            <div className={styles["trating"]}>
              <span>{info.averageRating}</span>
              <Rating
                name="half-rating-read"
                precision={1}
                className={styles["profileRatingStarts"]}
                value={Math.round(info.averageRating)}
                readOnly
                size={ratingSize}
              />
            </div>
          </div>
          <div className={styles["therapist-bio"]}>
            <div className={styles["bio-top"]}>
              <p>Bio</p>
              {editBio ? (
                <button onClick={handleSaveBio} className={styles["bio-btn"]}>
                  Save
                </button>
              ) : (
                <FiEdit onClick={handleEditBio} />
              )}
            </div>
            {editBio ? (
              <textarea
                type="text"
                className={styles["bioInput"]}
                onChange={(e) => {
                  setBio(e.target.value);
                }}
                value={bio}
              />
            ) : (
              <p>{info.bio}</p>
            )}
          </div>
          <div className={styles["therapist-lang"]}>
            <div className={styles["bio-top"]}>
              <p>Languages I speak</p>
              {editLanguage && (
                <button
                  onClick={handleSaveLanguage}
                  className={`${styles["bio-btn"]} ${styles.lang}`}
                >
                  Save
                </button>
              )}
            </div>
            <div className={styles["languages"]}>
              <div
                className={`${styles["language-div"]} ${
                  editLanguage ? styles.expand : ""
                }`}
              >
                {info?.languages?.map((language, index) => (
                  <>
                    <div className={styles["language"]}>
                      <span>{language}</span>
                    </div>
                    {editLanguage && (
                      <div
                        onClick={() => {
                          handleRemoveLanguage(index);
                        }}
                      >
                        <IoCloseOutline />
                      </div>
                    )}
                  </>
                ))}
                {editLanguage && (
                  <div className={styles["language-select"]}>
                    <Select
                      isMulti={true}
                      options={languages}
                      value={selectedLanguages}
                      onChange={handleLanguageChange}
                      menuPosition={"fixed"}
                      styles={{
                        menu: (provided, state) => ({
                          ...provided,
                          borderRadius: "20px",
                        }),
                        control: (provided, state) => ({
                          ...provided,
                          borderRadius: "20px",
                          outline: "none",
                          boxShadow: "none",
                        }),
                      }}
                    />
                  </div>
                )}
                {!editLanguage && (
                  <div className={styles["add"]}>
                    <IoMdAdd size={25} onClick={handleEditLanguage} />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles["divide"]}></div>
          <div className={styles["recent-notifications"]}>
            <p className={styles["head"]}>Latest notifications</p>
            {notifications.length > 0 ? (
              <div className={styles["recent-notifications"]}>
                {notifications.slice(0, 2).map((notification, index) => (
                  <div key={index} className={styles["recent-notification"]}>
                    {notification.category === "client" ? (
                      <img
                        src={avatar[notification.gender]}
                        alt="avatar"
                        style={{ height: "40px", width: "40px" }}
                      />
                    ) : (
                      <div className={styles["icon"]}>
                        {notification.category === "session" ? (
                          <BsCameraVideo color={"white"} size={20} />
                        ) : notification.category === "tasks" ? (
                          <IoMdAddCircle color={"white"} size={20} />
                        ) : notification.category === "case-notes" ? (
                          <FaRegPenToSquare color={"white"} size={20} />
                        ) : notification.category === "summary" ? (
                          <PiTrendUp color={"white"} size={20} />
                        ) : notification.category === "chat" ? (
                          <PiChatsTeardropBold color={"white"} size={20} />
                        ) : notification.category === "new" ? (
                          <FaRegUser color={"white"} size={20} />
                        ) : notification.category === "reschedule" ? (
                          <FaRegCalendarCheck color={"white"} size={20} />
                        ) : (
                          <PiCalendarXBold color={"white"} size={20} />
                        )}
                      </div>
                    )}
                    <p>{notification.notification}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p>No notification Found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TherapistProfile;
